<template>
  <v-dialog
    :value="open"
    max-width="1100"
    persistent
    scrollable
    transition="dialog-bottom-transition"
    @keydown="$event.key === 'Escape' && toggle()"
  >
    <v-card class="rounded-lg elevation-12">
      <v-toolbar :color="selectedStatus.color" dark flat>
        <v-toolbar-title class="text-h6 font-weight-medium">
          <v-icon left>mdi-account-heart</v-icon>
          Chăm sóc khách hàng
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="toggle()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-5">
        <div class="d-flex align-center mb-4">
          <v-avatar :color="selectedStatus.color" size="44" class="mr-3">
            <span class="white--text font-weight-bold">{{ getInitials(customer.name) }}</span>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-medium">{{ customer.name || 'Khách hàng' }}</div>
            <div class="text-caption grey--text text--darken-1">
              {{ customer.phone || 'Chưa có SĐT' }}
              <span v-if="customer.email"> - {{ customer.email }}</span>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip :color="selectedStatus.color" dark small>
            {{ selectedStatus.text }}
          </v-chip>
        </div>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab href="#form">
            <v-icon left small>mdi-content-save-edit</v-icon>
            Ghi chăm sóc
          </v-tab>
          <v-tab href="#history">
            <v-icon left small>mdi-history</v-icon>
            Lịch sử
            <v-chip x-small color="primary" dark class="ml-2">{{ sortedCareLogs.length }}</v-chip>
          </v-tab>
          <v-tab href="#reminders">
            <v-icon left small>mdi-bell-outline</v-icon>
            Nhắc hẹn
            <v-chip x-small color="indigo" dark class="ml-2">{{ sortedReminders.length }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <v-tab-item value="form">
            <v-form ref="form" v-model="valid" @submit.prevent="submit">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="status"
                    :items="activeStatuses"
                    item-text="text"
                    item-value="value"
                    label="Trạng thái chăm sóc"
                    prepend-inner-icon="mdi-flag"
                    outlined
                    dense
                    :rules="statusRules"
                    hide-details="auto"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-chip :color="item.color" dark x-small class="mr-2"></v-chip>
                          {{ item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item }">
                      <v-chip :color="item.color" dark small>{{ item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="careType"
                    :items="activeCareMethods"
                    item-text="text"
                    item-value="name"
                    label="Hình thức chăm sóc"
                    prepend-inner-icon="mdi-phone-message"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4">
                  <v-textarea
                    :value="customer.note || 'Chưa có thông tin chi tiết về khách hàng'"
                    label="Thông tin chi tiết khách hàng"
                    prepend-inner-icon="mdi-account-details"
                    outlined
                    dense
                    rows="5"
                    readonly
                    hide-details="auto"
                    class="customer-detail-field"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="content"
                    label="Nội dung chăm sóc"
                    prepend-inner-icon="mdi-note-edit"
                    outlined
                    dense
                    rows="5"
                    hide-details="auto"
                    placeholder="Ví dụ: Đã gọi tư vấn sản phẩm, khách cần thêm thông tin..."
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="customerFeedback"
                    label="Phản hồi của khách hàng"
                    prepend-inner-icon="mdi-comment-text"
                    outlined
                    dense
                    rows="5"
                    hide-details="auto"
                    placeholder="Ghi lại phản hồi, nhu cầu, mức độ quan tâm..."
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="feedbackStatus"
                    :items="activeFeedbackStatuses"
                    item-text="text"
                    item-value="value"
                    label="Trạng thái phản hồi khách hàng"
                    prepend-inner-icon="mdi-message-reply-text"
                    outlined dense clearable hide-details="auto"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip :color="item.color" dark small>{{ item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu
                    v-model="nextCareDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="nextCareDate"
                        label="Ngày chăm sóc tiếp"
                        prepend-inner-icon="mdi-calendar-clock"
                        outlined
                        dense
                        clearable
                        readonly
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="nextCareDate"
                      :min="today"
                      locale="vi"
                      @input="nextCareDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-form>
          </v-tab-item>

          <v-tab-item value="history">
            <v-timeline v-if="sortedCareLogs.length" dense align-top class="care-history">
              <v-timeline-item
                v-for="log in sortedCareLogs"
                :key="log.id"
                :color="getStatusColor(log.status)"
                small
              >
                <v-card outlined class="rounded-lg">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center flex-wrap mb-2">
                      <v-chip :color="getStatusColor(log.status)" dark small class="mr-2">
                        {{ getStatusText(log.status) }}
                      </v-chip>
                      <v-chip v-if="log.care_type" small outlined color="primary" class="mr-2">
                        {{ log.care_type }}
                      </v-chip>
                      <span class="text-caption grey--text text--darken-1">
                        {{ formatDate(log.created_at) }}
                      </span>
                    </div>

                    <div class="text-body-2 mb-2" v-if="log.content">
                      <strong>Nội dung:</strong> {{ log.content }}
                    </div>
                    <div class="text-body-2 mb-2" v-if="log.customer_feedback">
                      <strong>Phản hồi:</strong> {{ log.customer_feedback }}
                    </div>
                    <v-chip v-if="log.feedback_status" :color="getFeedbackStatus(log.feedback_status).color" dark x-small class="mb-2">
                      {{ getFeedbackStatus(log.feedback_status).text }}
                    </v-chip>
                    <div class="text-caption grey--text text--darken-1" v-if="log.next_care_date">
                      <v-icon x-small color="grey darken-1" class="mr-1">mdi-calendar-clock</v-icon>
                      Chăm tiếp: {{ formatDate(log.next_care_date) }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>

            <div v-else class="empty-state">
              <v-icon size="56" color="grey lighten-1">mdi-history</v-icon>
              <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Chưa có lịch sử chăm sóc</div>
            </div>
          </v-tab-item>

          <v-tab-item value="reminders">
            <v-list v-if="sortedReminders.length" two-line class="pa-0">
              <v-list-item
                v-for="reminder in sortedReminders"
                :key="reminder.id"
                class="reminder-item"
              >
                <v-list-item-avatar>
                  <v-avatar :color="getReminderColor(reminder)" size="36">
                    <v-icon dark small>{{ reminder.is_done ? 'mdi-check' : 'mdi-bell' }}</v-icon>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center flex-wrap">
                    <v-chip :color="getReminderColor(reminder)" dark small class="mr-2">
                      {{ getReminderText(reminder) }}
                    </v-chip>
                    {{ formatDate(reminder.reminder_date) }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ reminder.note || 'Nhắc chăm sóc khách hàng' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="reminder.status === 'pending' && !reminder.is_done">
                  <div v-if="editingReminderId === reminder.id" class="d-flex align-center">
                    <v-text-field v-model="rescheduleDate" type="date" :min="today" dense outlined
                      hide-details class="mr-2 reminder-date-input"></v-text-field>
                    <v-btn icon color="primary" :loading="reminderLoading" @click="saveReschedule(reminder)">
                      <v-icon>mdi-content-save</v-icon>
                    </v-btn>
                    <v-btn icon @click="cancelReschedule"><v-icon>mdi-close</v-icon></v-btn>
                  </div>
                  <div v-else class="d-flex">
                    <v-btn icon small color="indigo" title="Đổi lịch" @click="startReschedule(reminder)">
                      <v-icon small>mdi-calendar-edit</v-icon>
                    </v-btn>
                    <v-btn icon small color="green" title="Hoàn thành" @click="completeReminder(reminder)">
                      <v-icon small>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn icon small color="red" title="Hủy lịch" @click="cancelReminder(reminder)">
                      <v-icon small>mdi-calendar-remove</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <div v-else class="empty-state">
              <v-icon size="56" color="grey lighten-1">mdi-bell-off-outline</v-icon>
              <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Chưa có nhắc hẹn</div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text color="grey darken-1" :disabled="loading" @click="toggle()">
          <v-icon left>mdi-cancel</v-icon>
          Hủy bỏ
        </v-btn>
        <v-btn
          v-if="activeTab === 'form'"
          :color="selectedStatus.color"
          dark
          :loading="loading"
          :disabled="!valid || loading"
          class="px-6"
          elevation="2"
          @click="submit"
        >
          <v-icon left>mdi-content-save</v-icon>
          Lưu lịch sử
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import customerModule from '@/api/customers'

const customerActions = customerModule.actions

export default {
  props: {
    open: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: () => ({})
    },
    statuses: {
      type: Array,
      default: () => []
    },
    feedbackStatuses: {
      type: Array,
      default: () => []
    },
    careMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeTab: 'form',
      valid: true,
      loading: false,
      status: null,
      careType: null,
      content: '',
      customerFeedback: '',
      feedbackStatus: null,
      nextCareDate: null,
      nextCareDateMenu: false,
      editingReminderId: null,
      rescheduleDate: null,
      reminderLoading: false,
      statusRules: [
        v => (v === 0 || !!v) || 'Vui lòng chọn trạng thái chăm sóc'
      ]
    }
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.currentUser || JSON.parse(localStorage.getItem('user_info') || '{}')
    },
    saleId() {
      return this.currentUser?.id || this.currentUser?.user_id || this.customer?.user_id || null
    },
    sortedCareLogs() {
      return this.sortByDate(this.customer?.care_logs, 'created_at', 'desc')
    },
    sortedReminders() {
      return this.sortByDate(this.customer?.reminders, 'reminder_date', 'asc')
    },
    selectedStatus() {
      return this.getStatus(this.status)
    },
    activeStatuses() {
      return this.statuses.filter(item => item.is_active !== false)
    },
    activeFeedbackStatuses() {
      return this.feedbackStatuses.filter(item => item.is_active !== false)
    },
    activeCareMethods() {
      return this.careMethods.filter(item => item.is_active !== false)
    },
    today() {
      const date = new Date()
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().substr(0, 10)
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.populateDefaultStatus()
      }
    },
    customer: {
      handler() {
        if (this.open) {
          this.populateDefaultStatus()
        }
      },
      immediate: true
    },
    statuses() {
      if (this.open && this.status === null) this.populateDefaultStatus()
    }
  },
  methods: {
    populateDefaultStatus() {
      const current = this.getLatestCareLog()?.status ?? this.customer?.current_status
      this.status = this.activeStatuses.some(item => Number(item.value) === Number(current))
        ? current
        : this.activeStatuses[0]?.value ?? null
      this.activeTab = 'form'
    },
    sortByDate(items, field, direction = 'desc') {
      if (!Array.isArray(items)) return []

      return [...items].sort((a, b) => {
        const dateA = new Date(a?.[field] || 0).getTime()
        const dateB = new Date(b?.[field] || 0).getTime()
        return direction === 'asc' ? dateA - dateB : dateB - dateA
      })
    },
    getLatestCareLog() {
      return this.sortedCareLogs[0] || null
    },
    getStatus(status) {
      return this.statuses.find(item => Number(item.value) === Number(status)) || {
        text: 'Chưa chăm sóc',
        color: 'grey'
      }
    },
    getStatusText(status) {
      return this.getStatus(status).text
    },
    getStatusColor(status) {
      return this.getStatus(status).color
    },
    getFeedbackStatus(status) {
      return this.feedbackStatuses.find(item => Number(item.value) === Number(status)) || {
        text: 'Không xác định', color: 'grey'
      }
    },
    getReminderState(reminder) {
      if (!reminder) return 'none'
      if (reminder.status === 'cancelled') return 'cancelled'
      if (reminder.status === 'completed' || reminder.is_done) return 'done'

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const reminderDate = new Date(reminder.reminder_date)
      reminderDate.setHours(0, 0, 0, 0)

      if (reminderDate < today) return 'overdue'
      if (reminderDate.getTime() === today.getTime()) return 'today'
      return 'upcoming'
    },
    getReminderColor(reminder) {
      const colors = {
        overdue: 'red darken-1',
        today: 'deep-orange',
        upcoming: 'indigo',
        done: 'green',
        cancelled: 'grey',
        none: 'grey'
      }

      return colors[this.getReminderState(reminder)] || colors.none
    },
    getReminderText(reminder) {
      const labels = {
        overdue: 'Quá hạn',
        today: 'Hôm nay',
        upcoming: 'Sắp tới',
        done: 'Đã xong',
        cancelled: 'Đã hủy',
        none: 'Không có'
      }

      return labels[this.getReminderState(reminder)] || labels.none
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'

      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }

      return new Date(dateString).toLocaleDateString('vi-VN', options)
    },
    getInitials(name) {
      if (!name) return 'KH'
      return name.split(' ').map(part => part[0]).join('').substring(0, 2).toUpperCase()
    },
    toggle() {
      if (!this.loading) {
        this.$emit('toggle')
        this.resetForm()
      }
    },
    resetForm() {
      this.activeTab = 'form'
      this.status = null
      this.careType = null
      this.content = ''
      this.customerFeedback = ''
      this.feedbackStatus = null
      this.nextCareDate = null
      this.nextCareDateMenu = false
      this.cancelReschedule()
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    submit() {
      if (!this.$refs.form.validate()) return

      if (!this.saleId) {
        this.$snackbar.error('Không xác định được nhân viên phụ trách. Vui lòng đăng nhập lại.')
        return
      }

      this.loading = true

      const payload = {
        customer_id: this.customer.id,
        sale_id: this.saleId,
        status: this.status,
        care_type: this.careType,
        content: this.content,
        customer_feedback: this.customerFeedback,
        feedback_status: this.feedbackStatus,
        next_care_date: this.nextCareDate
      }

      customerActions.storeCareLog(payload)
        .then(() => {
          this.$snackbar.success('Lưu lịch sử chăm sóc thành công!')
          this.$emit('refresh')
          this.$emit('toggle')
          this.resetForm()
        })
        .catch(error => {
          console.error('Care log error:', error)
          const message = error.response?.data?.message || 'Không thể lưu lịch sử chăm sóc. Vui lòng thử lại.'
          this.$snackbar.error(message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    startReschedule(reminder) {
      this.editingReminderId = reminder.id
      this.rescheduleDate = String(reminder.reminder_date || '').substring(0, 10)
    },
    cancelReschedule() {
      this.editingReminderId = null
      this.rescheduleDate = null
    },
    refreshReminderData(message) {
      this.$snackbar.success(message)
      this.$emit('refresh')
      this.cancelReschedule()
    },
    saveReschedule(reminder) {
      if (!this.rescheduleDate) return
      this.reminderLoading = true
      customerActions.updateReminder({
        id: reminder.id,
        reminder_date: this.rescheduleDate,
        note: reminder.note
      }).then(() => {
        reminder.reminder_date = this.rescheduleDate
        this.refreshReminderData('Đổi lịch hẹn thành công')
      })
        .catch(error => this.$snackbar.error(error.response?.data?.message || 'Không thể đổi lịch hẹn'))
        .finally(() => { this.reminderLoading = false })
    },
    completeReminder(reminder) {
      this.reminderLoading = true
      customerActions.completeReminder(reminder.id)
        .then(() => {
          reminder.status = 'completed'
          reminder.is_done = true
          this.refreshReminderData('Đã hoàn thành lịch hẹn')
        })
        .catch(error => this.$snackbar.error(error.response?.data?.message || 'Không thể hoàn thành lịch hẹn'))
        .finally(() => { this.reminderLoading = false })
    },
    cancelReminder(reminder) {
      this.reminderLoading = true
      customerActions.cancelReminder(reminder.id)
        .then(() => {
          reminder.status = 'cancelled'
          reminder.is_done = true
          this.refreshReminderData('Đã hủy lịch hẹn')
        })
        .catch(error => this.$snackbar.error(error.response?.data?.message || 'Không thể hủy lịch hẹn'))
        .finally(() => { this.reminderLoading = false })
    }
  }
}
</script>

<style scoped>
.v-card {
  overflow: hidden;
}

.v-btn {
  text-transform: none !important;
  letter-spacing: 0;
}

.care-history {
  max-height: 420px;
  overflow-y: auto;
}

.reminder-item {
  border: thin solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-bottom: 8px;
}

.reminder-date-input {
  max-width: 165px;
}

.empty-state {
  text-align: center;
  padding: 36px 16px;
}

.customer-detail-field >>> textarea {
  background-color: #f5f7fa;
  color: #37474f !important;
  font-weight: 500;
}
</style>
